import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/Tabs';
import { useEmployees } from '@/shared/hooks/useEmployees';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import {
  Interaction,
  InteractionType,
  FeedbackStatus,
  FeedbackInteraction,
  DebriefInteraction,
  PraiseInteraction,
} from '@momentum/types';
import { useGoals } from '@/shared/hooks/useGoals';
import { InteractionsTableTab } from './components/InteractionsTableTab';
import { PendingFeedbackAlert } from './components/PendingFeedbackAlert';
import type { FilterState, EmployeeMap } from './types/interaction-table.types';
import { handleInteractionRowClick } from '../utils/handleInteractionRowClick';

interface InteractionsTableProps {
  interactions: Interaction[];
  filters: FilterState;
  isLoading?: boolean;
}

export const InteractionsTable = ({
  interactions,
  filters,
  isLoading,
}: InteractionsTableProps) => {
  const { t } = useTranslation('interactions');
  const currentEmployee = useEmployeeContext();
  const { data: employees } = useEmployees({ isActive: true });
  const { data: goals = [] } = useGoals(currentEmployee._id);
  const navigate = useNavigate();

  // Create employee map for quick lookup
  const employeeMap = useMemo<EmployeeMap>(() => {
    const map = new Map();
    employees?.forEach((emp) => {
      map.set(emp._id, {
        name: `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`,
        imageUrl: emp.personalInfo.imageUrl,
      });
    });
    return map;
  }, [employees]);

  // Split interactions into created and participated, and sort by creation date
  const { createdInteractions, participatedInteractions } = useMemo(() => {
    const created: Interaction[] = [];
    const participated: Interaction[] = [];

    interactions.forEach((interaction) => {
      if (interaction.creatorId === currentEmployee._id) {
        created.push(interaction);
      } else if (interaction.type === InteractionType.DEBRIEF) {
        const debrief = interaction as DebriefInteraction;
        if (
          debrief.coreParticipants.includes(currentEmployee._id) ||
          debrief.otherParticipants?.includes(currentEmployee._id)
        ) {
          participated.push(interaction);
        }
      } else {
        // For praise and feedback, check if current user is receiver
        const praiseOrFeedback = interaction as
          | PraiseInteraction
          | FeedbackInteraction;
        if (praiseOrFeedback.receiverId === currentEmployee._id) {
          participated.push(interaction);
        }
      }
    });

    // Sort both arrays by creation date (newest first)
    const sortByDate = (a: Interaction, b: Interaction) => {
      return (
        new Date(b.metadata.createdAt).getTime() -
        new Date(a.metadata.createdAt).getTime()
      );
    };

    created.sort(sortByDate);
    participated.sort(sortByDate);

    return {
      createdInteractions: created,
      participatedInteractions: participated,
    };
  }, [interactions, currentEmployee._id]);

  // Check if there are pending feedback alerts
  const hasPendingFeedbackAlert = useMemo(() => {
    return participatedInteractions.some((interaction) => {
      if (interaction.type !== InteractionType.FEEDBACK) return false;
      const feedback = interaction as FeedbackInteraction;
      return (
        feedback.receiverId === currentEmployee._id &&
        feedback.status !== FeedbackStatus.FINISHED
      );
    });
  }, [participatedInteractions, currentEmployee._id]);

  return (
    <div className="space-y-6">
      {/* Feedback Alert */}
      {hasPendingFeedbackAlert && <PendingFeedbackAlert />}

      {/* Tabs */}
      <Tabs defaultValue="created" className="w-full">
        <TabsList className="w-full justify-start bg-muted/50 p-1">
          <TabsTrigger
            value="created"
            className="flex-1 md:flex-none data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <span className="font-medium">{t('createdByMe')}</span>
            <span className="ms-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
              {createdInteractions.length}
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="participated"
            className="flex-1 md:flex-none data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <span className="font-medium">{t('participatedIn')}</span>
            <span className="ms-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
              {participatedInteractions.length}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="created" className="mt-6">
          <InteractionsTableTab
            interactions={createdInteractions}
            filters={filters}
            currentEmployeeId={currentEmployee._id}
            employeeMap={employeeMap}
            goals={goals}
            isLoading={isLoading}
            onRowClick={(interaction: Interaction) =>
              handleInteractionRowClick({
                navigate,
                interaction,
              })
            }
          />
        </TabsContent>

        <TabsContent value="participated" className="mt-6">
          <InteractionsTableTab
            interactions={participatedInteractions}
            filters={filters}
            currentEmployeeId={currentEmployee._id}
            employeeMap={employeeMap}
            goals={goals}
            isLoading={isLoading}
            onRowClick={(interaction: Interaction) =>
              handleInteractionRowClick({
                navigate,
                interaction,
              })
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
